import { useEffect, useState } from "react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from "recharts";
import { Wallet, TrendingDown } from "lucide-react";
import { api } from "@/lib/api";

function fmtMoney(n) {
    if (n >= 1_000_000_000) return `$${(n / 1_000_000_000).toFixed(2)}B`;
    if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
    if (n >= 1_000) return `$${(n / 1_000).toFixed(0)}k`;
    return `$${n}`;
}

const STATUS_COLORS = { Approved: "#10B981", Pending: "#3B82F6", Rejected: "#EF4444", "In Review": "#D4AF37" };

export default function Commercial() {
    const [data, setData] = useState(null);
    useEffect(() => { api.get("/commercial").then((r) => setData(r.data)); }, []);
    if (!data) return <div className="kicker text-slate-500">Loading commercial workspace…</div>;

    const { contracts, commercial_docs, totals, cashflow } = data;

    return (
        <div className="space-y-8">
            <div>
                <div className="kicker text-[var(--sgsb-gold)]">Commercial</div>
                <h1 className="mt-2 font-display font-black text-white text-4xl tracking-tighter">Commercial Management</h1>
                <p className="mt-2 text-slate-400 text-sm font-body max-w-2xl">
                    Contracts, variation orders, payment certificates and cash flow across the SGS Bateman portfolio.
                </p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                {[
                    ["Contract Value", fmtMoney(totals.contract_value_usd)],
                    ["Committed", fmtMoney(totals.committed_usd)],
                    ["Variation Orders", totals.variance_orders],
                    ["Payment Certs.", totals.payment_certificates],
                    ["Open Claims", totals.claims],
                ].map(([k, v]) => (
                    <div key={k} className="glass hover-lift rounded-xl p-5">
                        <div className="absolute top-0 left-0 h-[2px] w-10 bg-[var(--sgsb-gold)]" />
                        <div className="kicker text-slate-400">{k}</div>
                        <div className="mt-3 font-display font-black text-white text-2xl tracking-tighter">{v}</div>
                    </div>
                ))}
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-3 gap-5">
                <div className="glass rounded-xl p-6 xl:col-span-2">
                    <div className="flex items-center justify-between">
                        <div>
                            <div className="kicker text-slate-400">Cash flow</div>
                            <h3 className="font-display font-black text-white text-xl mt-1 tracking-tight">Inflow · Outflow ($M)</h3>
                        </div>
                        <TrendingDown className="h-4 w-4 text-[var(--sgsb-gold)]" strokeWidth={1.5} />
                    </div>
                    <div className="h-[280px] mt-4 -ml-3">
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={cashflow}>
                                <CartesianGrid strokeDasharray="3 6" stroke="rgba(148,163,184,0.12)" vertical={false} />
                                <XAxis dataKey="month" stroke="#64748B" tick={{ fontSize: 11, fontFamily: "Manrope" }} />
                                <YAxis stroke="#64748B" tick={{ fontSize: 11, fontFamily: "Manrope" }} />
                                <Tooltip contentStyle={{ background: "#0F172A", border: "1px solid rgba(212,175,55,0.35)", borderRadius: 8, color: "#F8FAFC" }} />
                                <Legend wrapperStyle={{ color: "#94A3B8", fontFamily: "Manrope", fontSize: 12 }} />
                                <Bar dataKey="inflow" fill="#D4AF37" radius={[4, 4, 0, 0]} />
                                <Bar dataKey="outflow" fill="#3B82F6" radius={[4, 4, 0, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                <div className="glass rounded-xl p-5">
                    <div className="flex items-center gap-2">
                        <Wallet className="h-4 w-4 text-[var(--sgsb-gold)]" strokeWidth={1.5} />
                        <div className="kicker text-slate-400">Ledger</div>
                    </div>
                    <h3 className="font-display font-black text-white text-lg mt-1 tracking-tight">Commercial documents</h3>
                    <div className="mt-4 space-y-2 max-h-[280px] overflow-y-auto pr-1">
                        {commercial_docs.slice(0, 12).map((d) => (
                            <div key={d.id} className="p-3 rounded-md bg-slate-950/40 border border-slate-800 flex items-center justify-between">
                                <div>
                                    <div className="text-slate-100 text-sm font-body">{d.ref}</div>
                                    <div className="text-[11px] text-slate-500">{d.type} · {d.counterparty}</div>
                                </div>
                                <div className="text-right">
                                    <div className="text-slate-200 font-medium font-body text-sm">{fmtMoney(d.amount_usd)}</div>
                                    <span
                                        className="inline-block text-[10px] rounded-full px-1.5 py-0.5"
                                        style={{ background: `${STATUS_COLORS[d.status]}22`, color: STATUS_COLORS[d.status], border: `1px solid ${STATUS_COLORS[d.status]}55` }}
                                    >
                                        {d.status}
                                    </span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            <div className="glass rounded-xl overflow-hidden">
                <div className="p-4 border-b border-slate-800/60">
                    <h3 className="font-display font-black text-white tracking-tight">Contracts</h3>
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full text-sm font-body">
                        <thead>
                            <tr className="text-slate-500 kicker text-[10px]">
                                <th className="text-left px-4 py-3">Ref</th>
                                <th className="text-left px-4 py-3">Contractor</th>
                                <th className="text-left px-4 py-3">Project</th>
                                <th className="text-right px-4 py-3">Value</th>
                                <th className="text-right px-4 py-3">Committed</th>
                                <th className="text-left px-4 py-3">Status</th>
                                <th className="text-left px-4 py-3">Expires</th>
                            </tr>
                        </thead>
                        <tbody>
                            {contracts.map((c) => (
                                <tr key={c.id} className="border-t border-slate-800/60 hover:bg-slate-900/40 transition-colors duration-150">
                                    <td className="px-4 py-3 text-slate-100">{c.ref}</td>
                                    <td className="px-4 py-3 text-slate-200">{c.contractor}</td>
                                    <td className="px-4 py-3 text-slate-400">{c.project_code}</td>
                                    <td className="px-4 py-3 text-right text-slate-200">{fmtMoney(c.value_usd)}</td>
                                    <td className="px-4 py-3 text-right text-slate-400">{fmtMoney(c.committed_usd)}</td>
                                    <td className="px-4 py-3">
                                        <span className="text-[11px] rounded-full px-2 py-0.5 border border-slate-700 text-slate-300">{c.status}</span>
                                    </td>
                                    <td className="px-4 py-3 text-slate-400">{c.expires}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}
